import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Star, Pin, PinOff, Loader2, Filter, Trash2 } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Skeleton } from './ui/skeleton';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AdminTestimonialsPanel = () => {
  const { token } = useAuth();
  const { language } = useLanguage();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('5'); // '5' | 'all'
  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const params = filter === '5' ? '?rating=5&limit=100' : '?limit=100';
      const { data } = await axios.get(`${API}/admin/reviews${params}`, authHeaders);
      setReviews(data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load reviews');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, token]);

  useEffect(() => {
    Promise.resolve().then(() => reload());
  }, [reload]);

  const togglePin = async (review) => {
    try {
      const { data } = await axios.post(`${API}/admin/reviews/${review.id}/pin`, {}, authHeaders);
      setReviews((prev) => prev.map((r) => (r.id === review.id ? { ...r, is_testimonial: data.is_testimonial } : r)));
      toast.success(
        data.is_testimonial
          ? (language === 'fr' ? 'Épinglé en témoignage homepage' : 'Pinned as homepage testimonial')
          : (language === 'fr' ? 'Désépinglé' : 'Unpinned'),
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Toggle failed');
    }
  };

  const deleteReview = async (review) => {
    const msg = language === 'fr'
      ? 'Supprimer définitivement cet avis ? La note du produit sera recalculée.'
      : 'Permanently delete this review? The product rating will be recalculated.';
    if (!window.confirm(msg)) return;
    try {
      await axios.delete(`${API}/admin/reviews/${review.id}`, authHeaders);
      setReviews((prev) => prev.filter((r) => r.id !== review.id));
      toast.success(language === 'fr' ? 'Avis supprimé, note du produit recalculée' : 'Review deleted, product rating recalculated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  const pinnedCount = reviews.filter((r) => r.is_testimonial).length;

  return (
    <Card data-testid="admin-testimonials-panel">
      <CardHeader>
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Star className="w-5 h-5 text-[#E9C46A]" />
              {language === 'fr' ? 'Témoignages homepage' : 'Homepage testimonials'}
            </CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              {language === 'fr'
                ? `Épinglez les meilleurs avis pour les afficher sur la page d'accueil (${pinnedCount} épinglé${pinnedCount > 1 ? 's' : ''}).`
                : `Pin the best reviews to show them on the homepage (${pinnedCount} pinned).`}
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              variant={filter === '5' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilter('5')}
              data-testid="testimonials-filter-5stars"
            >
              <Filter className="w-3 h-3 mr-1" /> 5 ★
            </Button>
            <Button
              variant={filter === 'all' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilter('all')}
              data-testid="testimonials-filter-all"
            >
              {language === 'fr' ? 'Tous' : 'All'}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="space-y-3">
            {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-20 w-full" />)}
          </div>
        ) : reviews.length === 0 ? (
          <p className="text-sm text-muted-foreground py-8 text-center">
            {language === 'fr' ? 'Aucun avis pour ce filtre.' : 'No reviews for this filter.'}
          </p>
        ) : (
          <div className="space-y-3">
            {reviews.map((r) => (
              <div
                key={r.id}
                className={`flex items-start gap-4 p-4 rounded-lg border ${r.is_testimonial ? 'border-[#E9C46A] bg-amber-50/30' : 'border-border'}`}
                data-testid={`admin-review-row-${r.id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className="font-semibold text-sm">{r.user_name}</span>
                    <div className="flex items-center gap-0.5">
                      {[...Array(r.rating || 0)].map((_, i) => (
                        <Star key={i} className="w-3 h-3 fill-[#E9C46A] text-[#E9C46A]" />
                      ))}
                    </div>
                    {r.product_title && (
                      <Badge variant="outline" className="text-xs">{r.product_title}</Badge>
                    )}
                    {r.is_testimonial && (
                      <Badge className="bg-[#E9C46A] text-[#0F4C5C] text-xs">
                        {language === 'fr' ? 'Homepage' : 'Homepage'}
                      </Badge>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground line-clamp-3">{r.comment || '—'}</p>
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    size="sm"
                    variant={r.is_testimonial ? 'outline' : 'default'}
                    onClick={() => togglePin(r)}
                    data-testid={`admin-pin-review-${r.id}`}
                  >
                    {r.is_testimonial ? (
                      <><PinOff className="w-4 h-4 mr-1" /> {language === 'fr' ? 'Retirer' : 'Unpin'}</>
                    ) : (
                      <><Pin className="w-4 h-4 mr-1" /> {language === 'fr' ? 'Épingler' : 'Pin'}</>
                    )}
                  </Button>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="text-destructive"
                    title={language === 'fr' ? 'Supprimer cet avis' : 'Delete this review'}
                    onClick={() => deleteReview(r)}
                    data-testid={`admin-delete-review-${r.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
